import { ReactElement } from 'react'
import { FormControlLabel, FormGroup, MenuItem, Switch, Tooltip } from '@mui/material'
import { useAppStore } from '@stores'
import { appStoreInitialState, defaultLoadRenderConfig } from 'src/state/stores/app'
import LoadRenderNumberField from './components/LoadRenderNumberField'
import LoadRenderSectionDivider from './components/LoadRenderSectionDivider'

/** Load display settings, grouped by line / point loads in 3D and in the 2D floorplan. */
const LoadRenderConfigMenuItems = (): ReactElement => {
  const lineLoadRenderConfig = useAppStore(state => state.loadRenderConfig.lineLoads)
  const pointLoadRenderConfig = useAppStore(state => state.loadRenderConfig.pointLoads)
  const setLineLoadRenderConfig = useAppStore(state => state.setLineLoadRenderConfig)
  const setPointLoadRenderConfig = useAppStore(state => state.setPointLoadRenderConfig)
  const envelopeRenderConfig = useAppStore(state => state.loadRenderConfig.innerForcesEnvelope)
  const setEnvelopeRenderConfig = useAppStore(state => state.setInnerForcesEnvelopeRenderConfig)

  return (
    <>
      <LoadRenderSectionDivider title="Linienlasten 3D" />

      <LoadRenderNumberField
        name="lineLoadTextScale"
        label="Textgröße für Linienlasten"
        tooltip={`Textgröße für Linienlasten in der Scene (default: ${defaultLoadRenderConfig.lineLoads.textScale})`}
        value={lineLoadRenderConfig.textScale}
        defaultValue={appStoreInitialState.loadRenderConfig.lineLoads.textScale}
        onChange={textScale => setLineLoadRenderConfig({ textScale })}
        dataCy="line-load-text-scale-input"
      />

      <MenuItem data-cy="btn-line-load-units-in-scene">
        <FormGroup>
          <Tooltip title="Einheiten für Linienlasten in der Szene anzeigen" placement="left">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={lineLoadRenderConfig.showUnitsInScene}
                  onChange={e => setLineLoadRenderConfig({ showUnitsInScene: e.target.checked })}
                />
              }
              label="Einheiten für Linienlasten anzeigen"
            />
          </Tooltip>
        </FormGroup>
      </MenuItem>

      <MenuItem data-cy="btn-line-load-unit-new-line">
        <FormGroup>
          <Tooltip title="Einheit für Linienlasten in separater Zeile anzeigen" placement="left">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={lineLoadRenderConfig.showUnitInNewLine}
                  onChange={e => setLineLoadRenderConfig({ showUnitInNewLine: e.target.checked })}
                  disabled={!lineLoadRenderConfig.showUnitsInScene}
                />
              }
              label="Einheit für Linienlasten in neuer Zeile"
            />
          </Tooltip>
        </FormGroup>
      </MenuItem>

      <LoadRenderSectionDivider title="Linienlasten 2D" />

      <LoadRenderNumberField
        name="lineLoadFloorplanMaxWidth"
        label="Max. Breite für Linienlasten im Grundriss [m]"
        tooltip={`Breite der größten Linienlast des Projekts im Grundriss, alle anderen werden proportional skaliert (default: ${defaultLoadRenderConfig.lineLoads.floorplanMaxWidth} m)`}
        value={lineLoadRenderConfig.floorplanMaxWidth}
        defaultValue={appStoreInitialState.loadRenderConfig.lineLoads.floorplanMaxWidth}
        onChange={floorplanMaxWidth => setLineLoadRenderConfig({ floorplanMaxWidth })}
        dataCy="line-load-floorplan-max-width-input"
      />

      <LoadRenderNumberField
        name="lineLoadFloorplanMinWidth"
        label="Min. Breite für Linienlasten im Grundriss [m]"
        tooltip={`Kleinste Breite einer Linienlast im Grundriss, damit auch kleine Lasten sichtbar bleiben (default: ${defaultLoadRenderConfig.lineLoads.floorplanMinWidth} m)`}
        value={lineLoadRenderConfig.floorplanMinWidth}
        defaultValue={appStoreInitialState.loadRenderConfig.lineLoads.floorplanMinWidth}
        onChange={floorplanMinWidth => setLineLoadRenderConfig({ floorplanMinWidth })}
        dataCy="line-load-floorplan-min-width-input"
      />

      <LoadRenderNumberField
        name="lineLoadFloorplanLineWidth"
        label="Linienstärke für Linienlasten im Grundriss [px]"
        tooltip={`Linienstärke der Umrisse und Pfeile von Linienlasten im Grundriss (default: ${defaultLoadRenderConfig.lineLoads.floorplanLineWidth} px)`}
        value={lineLoadRenderConfig.floorplanLineWidth}
        defaultValue={appStoreInitialState.loadRenderConfig.lineLoads.floorplanLineWidth}
        onChange={floorplanLineWidth => setLineLoadRenderConfig({ floorplanLineWidth })}
        dataCy="line-load-floorplan-line-width-input"
      />

      <LoadRenderNumberField
        name="lineLoadFloorplanTextScale"
        label="Textgröße für Linienlasten im Grundriss"
        tooltip={`Textgröße für Linienlasten im Grundriss (default: ${defaultLoadRenderConfig.lineLoads.floorplanTextScale})`}
        value={lineLoadRenderConfig.floorplanTextScale}
        defaultValue={appStoreInitialState.loadRenderConfig.lineLoads.floorplanTextScale}
        onChange={floorplanTextScale => setLineLoadRenderConfig({ floorplanTextScale })}
        dataCy="line-load-floorplan-text-scale-input"
      />

      <LoadRenderSectionDivider title="Punktlasten 3D" />

      <LoadRenderNumberField
        name="pointLoadTextScale"
        label="Textgröße für Punktlasten"
        tooltip={`Textgröße für Punktlasten in der Scene (default: ${defaultLoadRenderConfig.pointLoads.textScale})`}
        value={pointLoadRenderConfig.textScale}
        defaultValue={appStoreInitialState.loadRenderConfig.pointLoads.textScale}
        onChange={textScale => setPointLoadRenderConfig({ textScale })}
        dataCy="point-load-text-scale-input"
      />

      <MenuItem data-cy="btn-point-load-units-in-scene">
        <FormGroup>
          <Tooltip title="Einheiten für Punktlasten in der Szene anzeigen" placement="left">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={pointLoadRenderConfig.showUnitsInScene}
                  onChange={e => setPointLoadRenderConfig({ showUnitsInScene: e.target.checked })}
                />
              }
              label="Einheiten für Punktlasten anzeigen"
            />
          </Tooltip>
        </FormGroup>
      </MenuItem>

      <MenuItem data-cy="btn-point-load-unit-new-line">
        <FormGroup>
          <Tooltip title="Einheit für Punktlasten in separater Zeile anzeigen" placement="left">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={pointLoadRenderConfig.showUnitInNewLine}
                  onChange={e => setPointLoadRenderConfig({ showUnitInNewLine: e.target.checked })}
                  disabled={!pointLoadRenderConfig.showUnitsInScene}
                />
              }
              label="Einheit für Punktlasten in neuer Zeile"
            />
          </Tooltip>
        </FormGroup>
      </MenuItem>

      <LoadRenderSectionDivider title="Punktlasten 2D" />

      <LoadRenderNumberField
        name="pointLoadFloorplanMaxDiameter"
        label="Max. Durchmesser für Punktlasten im Grundriss [m]"
        tooltip={`Durchmesser des Kreises der größten Punktlast des Projekts im Grundriss, alle anderen werden proportional skaliert (default: ${defaultLoadRenderConfig.pointLoads.floorplanMaxDiameter} m)`}
        value={pointLoadRenderConfig.floorplanMaxDiameter}
        defaultValue={appStoreInitialState.loadRenderConfig.pointLoads.floorplanMaxDiameter}
        onChange={floorplanMaxDiameter => setPointLoadRenderConfig({ floorplanMaxDiameter })}
        dataCy="point-load-floorplan-diameter-input"
      />

      <LoadRenderNumberField
        name="pointLoadFloorplanMinDiameter"
        label="Min. Durchmesser für Punktlasten im Grundriss [m]"
        tooltip={`Kleinster Kreisdurchmesser einer Punktlast im Grundriss, damit auch kleine Lasten sichtbar bleiben (default: ${defaultLoadRenderConfig.pointLoads.floorplanMinDiameter} m)`}
        value={pointLoadRenderConfig.floorplanMinDiameter}
        defaultValue={appStoreInitialState.loadRenderConfig.pointLoads.floorplanMinDiameter}
        onChange={floorplanMinDiameter => setPointLoadRenderConfig({ floorplanMinDiameter })}
        dataCy="point-load-floorplan-min-diameter-input"
      />

      <LoadRenderNumberField
        name="pointLoadFloorplanTextScale"
        label="Textgröße für Punktlasten im Grundriss"
        tooltip={`Textgröße für Punktlasten im Grundriss (default: ${defaultLoadRenderConfig.pointLoads.floorplanTextScale})`}
        value={pointLoadRenderConfig.floorplanTextScale}
        defaultValue={appStoreInitialState.loadRenderConfig.pointLoads.floorplanTextScale}
        onChange={floorplanTextScale => setPointLoadRenderConfig({ floorplanTextScale })}
        dataCy="point-load-floorplan-text-scale-input"
      />

      <LoadRenderSectionDivider title="Umhüllende 3D" />

      <LoadRenderNumberField
        name="envelopeMaxOrdinate"
        label="Max. Ordinate Umhüllende in 3D"
        unit="m"
        tooltip={`Größte Ordinate der Umhüllenden (My, Vz und uz) in der 3D-Ansicht; der größte Wert eines Bauteils wird mit dieser Länge dargestellt (default: ${defaultLoadRenderConfig.innerForcesEnvelope.maxOrdinate} m)`}
        value={envelopeRenderConfig.maxOrdinate}
        defaultValue={appStoreInitialState.loadRenderConfig.innerForcesEnvelope.maxOrdinate}
        onChange={maxOrdinate => setEnvelopeRenderConfig({ maxOrdinate })}
        dataCy="envelope-max-ordinate-input"
      />
    </>
  )
}

export default LoadRenderConfigMenuItems
